import { authBase, apiBase } from 'api/api';
import {
    AuthenticateResponse,
    AuthenticateRequest,
    ValidateTokenRequest,
} from 'api/interface/authenticate';
import { AuthenticationResult } from '@azure/msal-browser';

const authenticate = async (
    req: AuthenticateRequest
): Promise<AuthenticateResponse> => {
    const res = await authBase.post(`/v4/login`, {
        user_name: req.userName,
        password: req.password,
    });
    if (res?.status !== 200) {
        let errorMessage = null;

        if (
            res?.data?.error ===
            'User account is inactive. Please contact support.'
        ) {
            errorMessage = res.data.error;
        }

        return {
            authenticated: false,
            token: '',
            organisation_id: 0,
            organisation_logo: '',
            user_id: 0,
            username: '',
            role_permissions: {},
            backoffice_organisation: false,
            backoffice_organisation_id: 0,
            belongs_to_backoffice: null,
            email: '',
            error: errorMessage,
            tokenExpiry: 0,
        };
    }

    const payload = res.data[0];
    const { user_details, role_permissions } = payload;

    return {
        authenticated: true,
        token: user_details.token,
        organisation_id: user_details.organisation_id,
        user_id: user_details.user_id,
        username: user_details.email,
        role_permissions,
        email: user_details.email,
        tokenExpiry: user_details.TokenExpires,
        backoffice_organisation: user_details.backoffice_organisation,
        backoffice_organisation_id: user_details.backoffice_organisation_id,
        belongs_to_backoffice: user_details.belongs_to_backoffice ?? null,
        organisation_logo: user_details.organisation_logo,
    };
};

const forgotPassword = async (email: string): Promise<boolean> => {
    const res = await authBase.post('/forgotPassword', {
        email,
    });
    if (res?.status === 200) {
        return true;
    }
    return false;
};

const changePassword = async (password: string, new_password: string) => {
    const res = await apiBase.put('v3/auth/update/password', {
        password,
        new_password,
    });

    if (res.status === 200) {
        return true;
    }
    return false;
};

const updateProfile = async ({
    email,
    username,
}: {
    email: string;
    username: string;
}) => {
    const res = await apiBase.put('v3/auth/profile', {
        email,
        username,
    });

    if (res.status === 200) {
        return true;
    }
    return false;
};

type SessionCheckResult = 'valid' | 'invalid' | 'error';

const checkSession = async (): Promise<SessionCheckResult> => {
    try {
        const res = await apiBase.get('/v3/auth/profile');
        if (res?.status === 200) {
            return 'valid';
        }
        if (res?.status === 401 || res?.status === 403) {
            return 'invalid';
        }
        return 'error';
    } catch {
        return 'error';
    }
};

const validateToken = async (req: ValidateTokenRequest): Promise<number> => {
    try {
        const res = await authBase.get(`/validate/${req.token}`);
        if (!res?.status) {
            return 0;
        }
        return res.status;
    } catch {
        return 0;
    }
};

const refreshToken = async (): Promise<{
    token: string;
    tokenExpiry: number;
}> => {
    const res = await apiBase.get('/v4/refresh-token');
    return { token: res.data.AccessToken, tokenExpiry: res.data.AtExpires };
};

const authenticateEntraId = async (
    req: AuthenticationResult
): Promise<AuthenticateResponse> => {
    const res = await authBase.post(`/v4/entraid/login`, {
        access_token: req.accessToken,
        id_token: req.idToken,
    });
    if (res?.status !== 200) {
        const errorMessage = res?.data?.error
            ? res?.data?.error
            : 'User account is inactive. Please contact support.';
        return {
            authenticated: false,
            token: '',
            organisation_id: 0,
            organisation_logo: '',
            user_id: 0,
            username: '',
            role_permissions: {},
            backoffice_organisation: false,
            backoffice_organisation_id: 0,
            belongs_to_backoffice: null,
            email: '',
            error: errorMessage,
            tokenExpiry: 0,
        };
    }
    const payload = res.data[0];
    const { user_details, role_permissions } = payload;
    return {
        authenticated: true,
        token: user_details.token,
        organisation_id: user_details.organisation_id,
        user_id: user_details.user_id,
        username: user_details.email,
        role_permissions,
        email: user_details.email,
        tokenExpiry: user_details.TokenExpires,
        backoffice_organisation: user_details.backoffice_organisation,
        backoffice_organisation_id: user_details.backoffice_organisation_id,
        belongs_to_backoffice: user_details.belongs_to_backoffice ?? null,
        organisation_logo: user_details.organisation_logo,
    };
};

const logout = async () => {
    const res = await apiBase.post('/v3/auth/logout');

    if (res.status === 200) {
        return true;
    }
    return false;
};

export const authApi = {
    authenticate,
    checkSession,
    validateToken,
    refreshToken,
    forgotPassword,
    changePassword,
    updateProfile,
    authenticateEntraId,
    logout,
};
